// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage from "@hardwoods/MobiusOverrides/LazyImage/LazyImage";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

enum fields {
    maximumNumberOfContentFeatures = "maximumNumberOfContentFeatures",
    leftImage = "leftImage",
    leftImageAlt = "leftImageAlt",
    leftTitle = "leftTitle",
    leftDescription = "leftDescription",
    leftLabel = "leftLabel",
    leftLink = "leftLink",

    rightImage = "rightImage",
    rightImageAlt = "rightImageAlt",
    rightTitle = "rightTitle",
    rightDescription = "rightDescription",
    rightLabel = "rightLabel",
    rightLink = "rightLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.maximumNumberOfContentFeatures]: number;

        [fields.leftImage]: string;
        [fields.leftImageAlt]: string;
        [fields.leftTitle]: string;
        [fields.leftDescription]: string;
        [fields.leftLabel]: string;
        [fields.leftLink]: LinkFieldValue;

        [fields.rightImage]: string;
        [fields.rightImageAlt]: string;
        [fields.rightTitle]: string;
        [fields.rightDescription]: string;
        [fields.rightLabel]: string;
        [fields.rightLink]: LinkFieldValue;
    };
}

export interface ContentFeatureStyles {
    centeringWrapper?: InjectableCss;
    container?: GridContainerProps;
    itemWrapper?: GridItemProps;
    contentWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    title?: TypographyProps;
    image?: LazyImageProps;
    description?: TypographyPresentationProps;
}

export const contentFeatureStyles: ContentFeatureStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    itemWrapper: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            flex-direction: column;
            position: relative;
            ${xScreen.lte("sm")} {
                align-items: center;
            }
        `,
    },
    contentWrapper: {
        css: css`
            background: ${color("common.background")};
            padding: 20px;
            border: 1px solid ${color("common.border")};
            border-radius: 6px;
            z-index: 999;
            margin-top: -100px;
            margin-left: 30px;
            margin-right: 30px;
            ${xScreen.lte("sm")} {
                max-width: 478px;
            }
        `,
    },
    link: {
        color: "common.white",
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0.25px;
                line-height: 22px;
            `,
        },
        css: css`
            display: inline-block;
            text-align: center;
            padding: 10px 25px;
            margin-top: 20px;
            margin-bottom: 5px;
            border-radius: 4px;
            background: ${color("primary")};
        `,
    },
    image: {
        width: "100%",
        height: "350px",
        css: css`
            max-height: 500px;
            ${xScreen.lte("sm")} {
                max-width: 536px;
            }
            img {
                border-radius: 4px;
                border: 1px solid transparent;
                max-height: 100%;
            }
        `,
    },
    title: {
        variant: "h3",
        as: "h1",
        css: css`
            line-height: 32px;
            ${xScreen.lte("sm")} {
                font-size: 20px;
                line-height: 20px;
            }
            ${xScreen.lte("xs")} {
                font-size: 18px;
                line-height: 5px;
            }
        `,
    },
    description: {
        size: 15,
        css: css`
            letter-spacing: 0;
            line-height: 26px;
            display: block;
        `,
    },
};

const styles = contentFeatureStyles;

interface SectionContentProps {
    image?: string;
    altText?: string;
    title?: string;
    description?: string;
    linkLabel?: string;
    linkUrl?: string;
    sectionPosition?: "leftSide" | "rightSide";
}

const SectionContent: FC<SectionContentProps> = ({
    image,
    altText,
    title,
    description,
    linkLabel,
    linkUrl,
    sectionPosition,
}) => {
    return (
        <GridItem {...styles.itemWrapper}>
            <LazyImage
                src={image}
                altText={altText}
                {...styles.image}
                cloudinaryTransformations={{ width: 625, quality: "good" }}
            />
            <StyledWrapper {...styles.contentWrapper} className={sectionPosition}>
                <Typography {...styles.title}>{title}</Typography>
                <Typography {...styles.description}>{description}</Typography>
                {linkLabel && (
                    <Link href={linkUrl} target="_blank" {...styles.link}>
                        {linkLabel}
                    </Link>
                )}
            </StyledWrapper>
        </GridItem>
    );
};

const ContentFeature: FC<OwnProps> = ({ fields }) => {
    const {
        maximumNumberOfContentFeatures,
        leftImage,
        leftImageAlt,
        leftTitle,
        leftDescription,
        leftLink,
        leftLabel,
        rightImage,
        rightImageAlt,
        rightTitle,
        rightDescription,
        rightLabel,
        rightLink,
    } = fields;

    return (
        <StyledWrapper {...styles.centeringWrapper}>
            <GridContainer {...styles.container}>
                {/* The left section of the content feature */}
                <SectionContent
                    image={leftImage}
                    altText={leftImageAlt || ""}
                    title={leftTitle}
                    description={leftDescription}
                    linkLabel={leftLabel}
                    linkUrl={leftLink?.value || ""}
                    sectionPosition="leftSide"
                />
                {/* The right section of the content feature */}
                {maximumNumberOfContentFeatures >= 2 && (
                    <SectionContent
                        image={rightImage}
                        altText={rightImageAlt || ""}
                        title={rightTitle}
                        description={rightDescription}
                        linkLabel={rightLabel}
                        linkUrl={rightLink?.value || ""}
                        sectionPosition="rightSide"
                    />
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const tabs = {
    left: {
        displayName: "Left Section",
        sortOrder: 0,
    },
    right: {
        displayName: "Right Section",
        sortOrder: 1,
    },
};

const getSectionCategoriesField = () => {
    const getFieldName = (name: string) => fields[name as keyof typeof fields];
    const isFieldVisible = (tab: string, maxNumber: number) => {
        if (tab === "left") {
            return maxNumber >= 1;
        }
        if (tab === "right") {
            return maxNumber >= 2;
        }
        return false;
    };

    const fieldsBase = (tab: "left" | "right") =>
        [
            {
                displayName: "Image",
                name: getFieldName(`${tab}Image`),
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tab: tabs[tab],
                isVisible: item => isFieldVisible(tab, item.fields.maximumNumberOfContentFeatures),
            },
            {
                displayName: "Image Alt",
                name: getFieldName(`${tab}ImageAlt`),
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: tabs[tab],
                isVisible: item => isFieldVisible(tab, item.fields.maximumNumberOfContentFeatures),
            },
            {
                displayName: "Title",
                name: getFieldName(`${tab}Title`),
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tab: tabs[tab],
                isVisible: item => isFieldVisible(tab, item.fields.maximumNumberOfContentFeatures),
            },
            {
                displayName: "Description",
                name: getFieldName(`${tab}Description`),
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tab: tabs[tab],
                isVisible: item => isFieldVisible(tab, item.fields.maximumNumberOfContentFeatures),
            },
            {
                displayName: "CTA Label",
                name: getFieldName(`${tab}Label`),
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: tabs[tab],
                isVisible: item => isFieldVisible(tab, item.fields.maximumNumberOfContentFeatures),
            },
            {
                displayName: "CTA Link",
                name: getFieldName(`${tab}Link`),
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: {
                    type: "Url",
                    value: "",
                },
                tab: tabs[tab],
                isVisible: item => isFieldVisible(tab, item.fields.maximumNumberOfContentFeatures),
            },
        ] as FieldDefinition[];

    const leftSectionCategoriesField = fieldsBase("left");
    const rightSectionCategoriesField = fieldsBase("right");
    return [leftSectionCategoriesField, rightSectionCategoriesField];
};

const [leftSectionCategory, rightSectionCategoriesField] = getSectionCategoriesField();
const widgetModule: WidgetModule = {
    component: ContentFeature,
    definition: {
        displayName: "Content Feature",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.maximumNumberOfContentFeatures,
                displayName: "Maximum number of Content Features",
                editorTemplate: "DropDownField",
                defaultValue: "2",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                ],
                hideEmptyOption: true,
                fieldType: "General",
            },
            ...leftSectionCategory,
            ...rightSectionCategoriesField,
        ],
    },
};

export default widgetModule;
